import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, Truck, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const slugify = (s) => encodeURIComponent(s);

export const Navbar = () => {
  const [tree, setTree] = useState({});
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setTree(r.data)).catch(() => {});
  }, []);

  const disciplines = Object.keys(tree);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/shop?q=${encodeURIComponent(q.trim())}`); setMobileOpen(false); }
  };

  const catLink = (d, c, s) =>
    `/shop?discipline=${slugify(d)}&category=${slugify(c)}${s ? `&subcategory=${slugify(s)}` : ""}`;

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top strip */}
      <div className="bg-primary text-primary-foreground">
        <div className="container-furn flex h-9 items-center justify-between text-[11px] font-medium tracking-wide">
          <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> Free UK delivery over £50 · Next-day available</div>
          <div className="hidden sm:flex items-center gap-4">
            <Link to="/delivery" className="hover:text-accent transition-colors">Delivery</Link>
            <Link to="/about" className="hover:text-accent transition-colors">Our Story</Link>
            <span>sales@furnangling.co.uk</span>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="border-b border-white/5 bg-background/85 backdrop-blur-xl">
        <div className="container-furn flex h-20 items-center gap-6">
          <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu className="h-6 w-6" />
          </button>
          <Logo />
          <form onSubmit={submitSearch} className="relative hidden flex-1 items-center md:flex max-w-xl">
            <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search rods, reels, bait, brands…"
              className="w-full border border-border bg-secondary/60 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-accent transition-colors"
            />
          </form>
          <div className="ml-auto flex items-center gap-1 sm:gap-3">
            <Link to={user ? "/account" : "/login"} data-testid="account-link" className="flex flex-col items-center px-2 text-foreground hover:text-accent transition-colors">
              <User className="h-5 w-5" />
              <span className="hidden lg:block text-[10px] font-medium mt-0.5">{user ? "Account" : "Sign In"}</span>
            </Link>
            <Link to="/wishlist" data-testid="wishlist-link" className="relative flex flex-col items-center px-2 text-foreground hover:text-accent transition-colors">
              <Heart className="h-5 w-5" />
              <span className="hidden lg:block text-[10px] font-medium mt-0.5">Wishlist</span>
              {wishCount > 0 && <span className="absolute -right-0.5 -top-1 grid h-4 w-4 place-items-center rounded-full bg-accent text-[9px] font-bold text-accent-foreground">{wishCount}</span>}
            </Link>
            <button data-testid="cart-button" onClick={() => setOpen(true)} className="relative flex flex-col items-center px-2 text-foreground hover:text-accent transition-colors">
              <ShoppingBag className="h-5 w-5" />
              <span className="hidden lg:block text-[10px] font-medium mt-0.5">Basket</span>
              {count > 0 && <span className="absolute -right-0.5 -top-1 grid h-4 w-4 place-items-center rounded-full bg-accent text-[9px] font-bold text-accent-foreground">{count}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop nav row with mega menu */}
      <nav className="hidden border-b border-white/5 bg-background/85 backdrop-blur-xl lg:block" onMouseLeave={() => setActive(null)}>
        <div className="container-furn flex h-12 items-center gap-8">
          {disciplines.map((d) => (
            <div key={d} onMouseEnter={() => setActive(d)} className="h-full">
              <Link
                to={`/shop?discipline=${slugify(d)}`}
                data-testid={`nav-${slugify(d)}`}
                className={`flex h-12 items-center gap-1 text-xs font-bold uppercase tracking-widest transition-colors ${active === d ? "text-accent" : "text-foreground hover:text-accent"}`}
              >
                {d} <ChevronDown className="h-3 w-3" />
              </Link>
            </div>
          ))}
          <Link to="/brands" data-testid="nav-brands" className="text-xs font-bold uppercase tracking-widest text-foreground hover:text-accent transition-colors">Brands</Link>
          <Link to="/bundles" data-testid="nav-bundles" className="text-xs font-bold uppercase tracking-widest text-accent hover:brightness-125 transition-[filter]">Deals</Link>
          <Link to="/blog" data-testid="nav-blog" className="text-xs font-bold uppercase tracking-widest text-foreground hover:text-accent transition-colors">Blog</Link>
          <Link to="/shop?on_offer=true" className="ml-auto text-xs font-bold uppercase tracking-widest text-destructive hover:brightness-125 transition-[filter]">Clearance</Link>
        </div>

        {active && tree[active] && (
          <div className="absolute left-0 w-full border-b border-border bg-popover shadow-2xl">
            <div className="container-furn grid grid-cols-4 gap-8 py-8">
              {Object.entries(tree[active]).map(([cat, subs]) => (
                <div key={cat}>
                  <Link to={catLink(active, cat)} onClick={() => setActive(null)} className="mb-3 block font-display text-sm font-semibold text-accent hover:text-foreground transition-colors">{cat}</Link>
                  <ul className="space-y-1.5">
                    {subs.slice(0, 7).map((s) => (
                      <li key={s}>
                        <Link to={catLink(active, cat, s)} onClick={() => setActive(null)} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{s}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[88vw] max-w-sm overflow-y-auto bg-background p-0">
          <div className="flex items-center justify-between border-b border-border p-4">
            <Logo compact />
            <button onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></button>
          </div>
          <form onSubmit={submitSearch} className="relative flex items-center p-4">
            <Search className="absolute left-7 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="mobile-search-input" className="w-full border border-border bg-secondary/60 py-2.5 pl-9 pr-3 text-sm outline-none" />
          </form>
          <Accordion type="single" collapsible className="px-2">
            {disciplines.map((d) => (
              <AccordionItem key={d} value={d} className="border-border">
                <AccordionTrigger className="px-2 text-sm font-bold uppercase tracking-wide">{d}</AccordionTrigger>
                <AccordionContent>
                  <div className="grid gap-3 px-2 pb-2">
                    {Object.entries(tree[d]).map(([cat, subs]) => (
                      <div key={cat}>
                        <Link to={catLink(d, cat)} onClick={() => setMobileOpen(false)} className="block text-sm font-semibold text-accent mb-1">{cat}</Link>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {subs.slice(0, 6).map((s) => (
                            <Link key={s} to={catLink(d, cat, s)} onClick={() => setMobileOpen(false)} className="text-xs text-muted-foreground">{s}</Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="flex flex-col gap-1 border-t border-border p-4 text-sm">
            <Link to="/brands" onClick={() => setMobileOpen(false)} className="py-2">Brands</Link>
            <Link to="/bundles" onClick={() => setMobileOpen(false)} className="py-2 text-accent">Deals & Bundles</Link>
            <Link to="/blog" onClick={() => setMobileOpen(false)} className="py-2">Fishing Guides</Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="py-2">Our Story</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="py-2">Contact</Link>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Navbar;
